import { integrationApi } from "@repo/api";
import {
  Button,
  ButtonSelect,
  Chip,
  Icon,
  InlineMessage,
  Loader,
  MenuMulti,
  Typography,
} from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { theme } from "../../../constants/theme";
import { useUI } from "../../../contexts/UIContext";
import { DynamicRenderer } from "../../../core/DynamicRenderer";
import type { FormConfig } from "../../../types/form";
import { FormSection } from "../../FormSection";
import { CFTSection } from "./CFTSection";
import { areRegionsValid } from "./utils";

interface ConnectionDetailsSectionProps {
  config: FormConfig;
  cftDownloaded: boolean;
  showCftAlert: boolean;
  cftError: string | null;
  selectedRegions: Array<{ label: string; value: string; isChecked: boolean }>;
  onCftDownload: () => void;
  onCftAlertClose: () => void;
  onCftErrorClear: () => void;
  onRegionsChange: (
    regions: Array<{ label: string; value: string; isChecked: boolean }>,
  ) => void;
  roleArn?: string;
  externalId?: string;
  isEditMode?: boolean;
}

export function ConnectionDetailsSection({
  config,
  cftDownloaded,
  showCftAlert,
  cftError,
  selectedRegions,
  onCftDownload,
  onCftAlertClose,
  onCftErrorClear,
  onRegionsChange,
  roleArn,
  externalId,
  isEditMode,
}: ConnectionDetailsSectionProps) {
  const { formData } = useUI();
  const intl = useIntl();
  const regionOptions = config.regions || [];
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    "idle" | "success" | "failed"
  >("idle");
  const [hasRegionsInteracted, setHasRegionsInteracted] = useState(false);

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus("idle");

    try {
      const roleArn = formData.role_arn || "";
      const externalId = formData.external_id || "";
      if (!roleArn || !externalId) {
        setConnectionStatus("failed");
        return;
      }

      const response = await integrationApi.testConnection(
        "awsplatform",
        roleArn,
        externalId,
      );

      if (response.status === "VALID") {
        setConnectionStatus("success");
      } else {
        setConnectionStatus("failed");
      }
    } catch (error) {
      setConnectionStatus("failed");
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Separate fields by section
  const connectionDetailsFields = config.fields.filter(
    (f) => f.id !== "use_case" && f.id !== "services" && !f.visibleWhen,
  );

  // Fields that appear after CFT download
  const conditionalFields = config.fields.filter(
    (f) => f.visibleWhen === "cftDownloaded",
  );

  // Handle region selection change
  const handleRegionsChange = (
    newRegions: Array<{ label: string; value: string; isChecked: boolean }>,
  ) => {
    setHasRegionsInteracted(true);
    onRegionsChange(newRegions);
  };

  return (
    <FormSection
      icon="Connect"
      title={intl.formatMessage({
        id: "6c0sHj",
        defaultMessage: "What are the connection details of your integration?",
        description: "Connection details section title",
      })}
      subtitle={intl.formatMessage({
        id: "KlVUsw",
        defaultMessage:
          "Some fields have been pre-filled for you, you can update them whenever you like.",
        description: "Connection details section subtitle",
      })}
      className="p-4"
    >
      {connectionDetailsFields.map((field) => {
        // Add validation props for specific fields
        const fieldWithValidation = {
          ...field,
          ...(field.id === "account_id" && {
            regexPattern: "^\\d{12}$",
            // Disable Account ID field in edit mode
            ...(isEditMode && { isReadOnly: true }),
          }),
        };
        return (
          <div key={field.id}>
            <DynamicRenderer field={fieldWithValidation} />
          </div>
        );
      })}

      {/* Regions to Scan - Always visible */}
      {regionOptions.length > 0 && (
        <div className="flex flex-col gap-1">
          <Typography kind="label">
            <FormattedMessage
              id="bYCnEt"
              defaultMessage="Regions to Scan"
              description="Label for regions to scan field"
            />
            <span className="text-[var(--color-foreground-critical-bold,#d32f2f)] ml-1">
              <FormattedMessage
                id="+uGQkt"
                defaultMessage="*"
                description="Required field indicator"
              />
            </span>
          </Typography>
          <MenuMulti
            label={
              selectedRegions.length === 0
                ? intl.formatMessage({
                    id: "4IjC5v",
                    defaultMessage: "Select regions",
                    description: "Placeholder text for region selection",
                  })
                : selectedRegions.length === regionOptions.length
                  ? intl.formatMessage({
                      id: "S4sNZ0",
                      defaultMessage: "All regions selected",
                      description: "Label when all regions are selected",
                    })
                  : intl.formatMessage(
                      {
                        id: "IihGKG",
                        defaultMessage:
                          "{count} {count, plural, one {region} other {regions}} selected",
                        description: "Label showing count of selected regions",
                      },
                      { count: selectedRegions.length },
                    )
            }
            trigger={
              <ButtonSelect className="w-full [&_[class*='ButtonSelect-contentContainer']]:!justify-between" />
            }
            options={regionOptions.map((opt) => ({
              ...opt,
              isChecked: selectedRegions.some((r) => r.value === opt.value),
            }))}
            onChange={(selected) => {
              if (selected) {
                handleRegionsChange(
                  selected
                    .filter((opt) => opt.isChecked)
                    .map((opt) => ({
                      label: opt.label,
                      value: opt.value,
                      isChecked: true,
                    })),
                );
              }
            }}
            minOptionsForSearch={5}
            appendLabelsToButton={false}
          />
          {selectedRegions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedRegions.length === regionOptions.length ? (
                <Chip
                  size="medium"
                  label={intl.formatMessage({
                    id: "D0K/FU",
                    defaultMessage: "All",
                    description: "Chip label when all regions are selected",
                  })}
                  kind="outlined"
                  onClick={() => {
                    handleRegionsChange([]);
                  }}
                  rightIcon={<Icon kind="Close" />}
                />
              ) : (
                selectedRegions.map((option) => (
                  <Chip
                    key={option.value}
                    size="medium"
                    label={option.label}
                    kind="outlined"
                    onClick={() => {
                      const newRegions = selectedRegions.filter(
                        (item) => item.value !== option.value,
                      );
                      handleRegionsChange(newRegions);
                    }}
                    rightIcon={<Icon kind="Close" />}
                  />
                ))
              )}
            </div>
          )}
          {hasRegionsInteracted && !areRegionsValid(selectedRegions) && (
            <InlineMessage
              text={intl.formatMessage({
                id: "5Bd5Kv",
                defaultMessage: "Please select at least one region",
                description: "Regions to scan required error message",
              })}
              colorTheme="critical"
              size="small"
              leftIcon={<Icon kind="AlertCriticalFilled" />}
            />
          )}
        </div>
      )}

      {/* CFT Section */}
      <CFTSection
        showCftAlert={showCftAlert}
        cftError={cftError}
        onDownload={() => {
          // Validate regions before allowing CFT download
          if (!areRegionsValid(selectedRegions)) {
            setHasRegionsInteracted(true);
            return;
          }
          onCftDownload();
        }}
        onCloseAlert={onCftAlertClose}
        onErrorClear={onCftErrorClear}
        roleArn={roleArn}
        externalId={externalId}
        isEditMode={isEditMode}
        accountId={formData.account_id}
      />

      {/* Conditional Fields - Role ARN, External ID, and Test Connection */}
      {cftDownloaded && (
        <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
          {conditionalFields.map((field) => {
            // Add validation props for Role ARN and ensure fields are editable
            // Remove any value prop to ensure fields are controlled by form state
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { value, ...fieldWithoutValue } = field;
            const fieldWithValidation = {
              ...fieldWithoutValue,
              // Explicitly set isReadOnly to false to ensure fields are editable
              isReadOnly: false,
              ...(field.id === "role_arn" && {
                regexPattern:
                  "^arn:aws:iam::\\d{12}:role\\/[a-zA-Z0-9+=,.@_-]+$",
              }),
            };
            return (
              <div key={field.id}>
                <DynamicRenderer field={fieldWithValidation} />
              </div>
            );
          })}

          <div className="flex items-center gap-4">
            <Button
              kind="outlined"
              size="medium"
              onClick={handleTestConnection}
              isDisabled={
                !formData.role_arn ||
                !formData.external_id ||
                isTestingConnection
              }
            >
              <FormattedMessage
                id="ELP3Cl"
                defaultMessage="Test Connection"
                description="Test connection button label"
              />
            </Button>
            {isTestingConnection && (
              <InlineMessage
                size="medium"
                colorTheme="info"
                leftIcon={<Loader kind="loop" format="inline" color="info" />}
                text={intl.formatMessage({
                  id: "T13/xw",
                  defaultMessage: "Testing Connection",
                  description: "Testing connection message",
                })}
              />
            )}
            {connectionStatus === "success" && (
              <InlineMessage
                size="medium"
                colorTheme="success"
                leftIcon={<Icon kind="CheckCircleFilled" />}
                text={intl.formatMessage({
                  id: "TzdmbR",
                  defaultMessage: "Connection Succeeded",
                  description: "Connection success message",
                })}
              />
            )}
            {connectionStatus === "failed" && (
              <InlineMessage
                size="medium"
                colorTheme="critical"
                leftIcon={<Icon kind="AlertCriticalFilled" />}
                text={intl.formatMessage({
                  id: "CPldkc",
                  defaultMessage: "Connection Failed",
                  description: "Connection failed message",
                })}
              />
            )}
          </div>
        </div>
      )}
    </FormSection>
  );
}
